import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Collapse, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import { useTranslation } from 'react-i18next';

import { OverallResult, StepStatus, TestStep, useConnectionTest } from './useConnectionTest';
import { productName } from 'config/productProfile';
import { ModalTitle } from 'components/index';
import BrowserShield from './BrowserShield';
import CameraShield from './CameraShield';
import NetworkShield from './NetworkShield';
import BandwidthShield from './BandwidthShield';
import styles from './ConnectionTestDialog.module.scss';

interface Props {
  open: boolean;
  onClose(): void;
}

const STEP_LABEL_KEYS: Record<TestStep['id'], string> = {
  browser: 'stepBrowser',
  devices: 'stepDevices',
  network: 'stepNetwork',
  bandwidth: 'stepBandwidth',
};

const STATUS_COLOR: Record<StepStatus, string> = {
  idle: '#9E9E9E',
  running: '#9E9E9E',
  passed: '#4CAF50',
  warning: '#FF9800',
  failed: '#F44336',
};

const SHIELD_COMPONENTS: Record<TestStep['id'], React.ComponentType<{ color?: string }>> = {
  browser: BrowserShield,
  devices: CameraShield,
  network: NetworkShield,
  bandwidth: BandwidthShield,
};

function StepIcon({ id, status }: { id: TestStep['id']; status: StepStatus }) {
  if (status === 'running') return <CircularProgress size={32} />;
  const Shield = SHIELD_COMPONENTS[id];
  return <Shield color={STATUS_COLOR[status]} />;
}

function NetworkAdvancedDetails({ variant }: { variant: 'failed' | 'warning' }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('providerRoom');
  const ns = (key: string) => t(`connectionTest.${key}` as any);

  return (
    <div className={styles.advancedWrap}>
      <button className={styles.advancedToggle} onClick={() => setOpen((o) => !o)}>
        {open ? ns('networkAdvancedHide') : ns('networkAdvancedShow')}
      </button>
      <Collapse in={open}>
        <div className={styles.advancedPanel}>
          {variant === 'warning' && (
            <>
              <div className={styles.advancedSectionTitle}>{ns('networkAdvancedTurnWhyTitle')}</div>
              <p className={styles.advancedExplain}>{ns('networkAdvancedTurnExplain')}</p>
              <div className={styles.advancedSectionTitle} style={{ marginTop: '0.75rem' }}>
                {ns('networkAdvancedTurnTipsTitle')}
              </div>
              <ul className={styles.advancedTips}>
                <li>{ns('networkAdvancedTurnTip1')}</li>
                <li>{ns('networkAdvancedTurnTip2')}</li>
                <li>{ns('networkAdvancedTurnTip3')}</li>
              </ul>
            </>
          )}

          {variant === 'failed' && (
            <>
              <div className={styles.advancedSectionTitle}>{ns('networkAdvancedPortsTitle')}</div>
              <table className={styles.portTable}>
                <thead>
                  <tr>
                    <th>{ns('networkAdvancedProtocol')}</th>
                    <th>{ns('networkAdvancedPort')}</th>
                    <th>{ns('networkAdvancedPurpose')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>TCP / UDP</td>
                    <td>3478</td>
                    <td>{ns('networkAdvancedStun')}</td>
                  </tr>
                  <tr>
                    <td>TCP</td>
                    <td>443</td>
                    <td>{ns('networkAdvancedTurnTls')}</td>
                  </tr>
                  <tr>
                    <td>UDP</td>
                    <td>10,000 – 60,000</td>
                    <td>{ns('networkAdvancedMedia')}</td>
                  </tr>
                  <tr>
                    <td>TCP</td>
                    <td>443 (WSS)</td>
                    <td>{ns('networkAdvancedSignal')}</td>
                  </tr>
                </tbody>
              </table>
              <div className={styles.advancedSectionTitle} style={{ marginTop: '0.75rem' }}>
                {ns('networkAdvancedTipsTitle')}
              </div>
              <ul className={styles.advancedTips}>
                <li>{ns('networkAdvancedTip1')}</li>
                <li>{ns('networkAdvancedTip2')}</li>
                <li>{ns('networkAdvancedTip3')}</li>
              </ul>
            </>
          )}
        </div>
      </Collapse>
    </div>
  );
}

const OVERALL_BG: Record<NonNullable<OverallResult>, string> = {
  pass: 'success.main',
  warning: 'warning.main',
  fail: 'error.main',
};

const OVERALL_COLOR = 'common.white';

const SLOW_THRESHOLD_MS = 15000;

export function ConnectionTestDialog({ open, onClose }: Props) {
  const { t } = useTranslation('providerRoom');
  const ns = (key: string, params?: Record<string, string | number>) => t(`connectionTest.${key}` as any, params);

  const { steps, isRunning, isDone, overallResult, startTest, stopTest, reset } = useConnectionTest();
  const [isSlow, setIsSlow] = useState(false);

  const runningStepId = steps.find((s) => s.status === 'running')?.id;

  useEffect(() => {
    setIsSlow(false);
    if (!runningStepId) return;
    const timer = setTimeout(() => setIsSlow(true), SLOW_THRESHOLD_MS);
    return () => clearTimeout(timer);
  }, [runningStepId]);

  useEffect(() => {
    if (open) startTest();
    else reset();
  }, [open]); // intentionally omit startTest/reset — stable callbacks, only re-run on open toggle

  const handleClose = () => {
    if (!isRunning) onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='xs' fullWidth disableEscapeKeyDown={isRunning}>
      <DialogTitle>
        <ModalTitle title={ns('dialogTitle')} onClose={!isRunning ? handleClose : undefined} />
      </DialogTitle>

      <DialogContent>
        <div className={styles.stepList}>
          {steps.map((step) => (
            <div key={step.id} className={styles.stepRow}>
              <div className={styles.stepIcon}>
                <StepIcon id={step.id} status={step.status} />
              </div>
              <div className={styles.stepText}>
                <div className={styles.stepLabel}>{ns(STEP_LABEL_KEYS[step.id])}</div>
                {step.status === 'running' && (
                  <>
                    <div className={styles.stepDetail}>{ns('statusRunning')}</div>
                    {isSlow && step.id === runningStepId && <div className={styles.stepSlow}>{ns('statusSlow')}</div>}
                  </>
                )}
                {step.detailKey && step.status !== 'idle' && step.status !== 'running' && (
                  <div className={styles.stepDetail}>{ns(step.detailKey, step.detailParams)}</div>
                )}
                {step.detailKey2 && step.status !== 'idle' && step.status !== 'running' && (
                  <div className={styles.stepDetail}>{ns(step.detailKey2, step.detailParams2)}</div>
                )}
                {step.id === 'network' &&
                  (step.status === 'warning' ||
                    (step.status === 'failed' && step.detailKey !== 'detailNetworkOffline')) && (
                    <NetworkAdvancedDetails variant={step.status} />
                  )}
              </div>
            </div>
          ))}
        </div>

        {isDone && overallResult && (
          <>
            <Box className={styles.overallBanner} sx={{ bgcolor: OVERALL_BG[overallResult], color: OVERALL_COLOR }}>
              {overallResult === 'pass' && <CheckCircleIcon fontSize='small' />}
              {overallResult === 'warning' && <WarningAmberIcon fontSize='small' />}
              {overallResult === 'fail' && <ErrorIcon fontSize='small' />}
              <Typography variant='body2' fontWeight={600}>
                {ns(`overall${overallResult.charAt(0).toUpperCase() + overallResult.slice(1)}`)}
              </Typography>
            </Box>

            {(overallResult === 'warning' || overallResult === 'fail') && (
              <Typography variant='caption' className={styles.guidance} display='block'>
                {ns(overallResult === 'fail' ? 'guidanceFail' : 'guidanceWarn', { productName })}
              </Typography>
            )}
          </>
        )}

        <div className={styles.actions}>
          {isDone && (
            <Button variant='outlined' size='small' onClick={() => startTest()}>
              {ns('tryAgain')}
            </Button>
          )}
          {isRunning ? (
            <Button variant='contained' size='small' color='error' sx={{ color: 'common.white' }} onClick={stopTest}>
              {ns('stopTest')}
            </Button>
          ) : (
            <Button variant='contained' size='small' onClick={handleClose}>
              {ns('close')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
